import { Calendar, Filter, Eye, EyeOff } from 'lucide-react';
import { ChartDateRange } from '../../hooks/useChartsData';

interface ChartsFiltersProps {
  dateRange: ChartDateRange;
  showRevenue: boolean;
  showExpenses: boolean;
  onPeriodChange: (period: ChartDateRange['period']) => void;
  onDateRangeChange: (range: Partial<ChartDateRange>) => void;
  onToggleRevenue: () => void;
  onToggleExpenses: () => void;
}

const periodOptions = [
  { value: 'last7days', label: '7 derniers jours' },
  { value: 'last30days', label: '30 derniers jours' },
  { value: 'last3months', label: '3 derniers mois' },
  { value: 'last6months', label: '6 derniers mois' },
  { value: 'last12months', label: '12 derniers mois' },
  { value: 'thisYear', label: 'Cette année' },
  { value: 'lastYear', label: 'Année précédente' },
  { value: 'custom', label: 'Période personnalisée' }
] as const;

export function ChartsFilters({
  dateRange,
  showRevenue,
  showExpenses,
  onPeriodChange,
  onDateRangeChange,
  onToggleRevenue,
  onToggleExpenses
}: ChartsFiltersProps) {
  
  const handleCustomDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onDateRangeChange({
      [field]: value,
      period: 'custom'
    });
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Titre et icône */}
      <div className="flex items-center space-x-2">
        <Filter className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtres des graphiques</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Sélecteur de période */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Période
          </label>
          <select
            value={dateRange.period}
            onChange={(e) => onPeriodChange(e.target.value as ChartDateRange['period'])}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date de début (pour période personnalisée) */}
        {dateRange.period === 'custom' && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Date de début
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Date de fin (pour période personnalisée) */}
        {dateRange.period === 'custom' && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Date de fin
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Contrôles de visibilité */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Affichage
          </label>
          <div className="flex space-x-2">
            <button
              onClick={onToggleRevenue}
              className={`flex items-center space-x-1 px-3 py-2 text-xs font-medium rounded-md transition-colors duration-150 ${
                showRevenue 
                  ? 'bg-green-100 text-green-700 border border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700' 
                  : 'bg-gray-100 text-gray-500 border border-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
              }`}
            >
              {showRevenue ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              <span>CA</span>
            </button>
            
            <button
              onClick={onToggleExpenses}
              className={`flex items-center space-x-1 px-3 py-2 text-xs font-medium rounded-md transition-colors duration-150 ${
                showExpenses 
                  ? 'bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700' 
                  : 'bg-gray-100 text-gray-500 border border-gray-300 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
              }`}
            >
              {showExpenses ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              <span>Charges</span>
            </button>
          </div>
        </div>
      </div>

      {/* Résumé de la période sélectionnée */}
      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2">
        <span className="font-medium">Période analysée :</span> {' '}
        {new Date(dateRange.startDate).toLocaleDateString('fr-FR')} - {new Date(dateRange.endDate).toLocaleDateString('fr-FR')}
        {dateRange.period !== 'custom' && (
          <span className="ml-2 text-primary font-medium">
            ({periodOptions.find(p => p.value === dateRange.period)?.label})
          </span>
        )}
      </div>
    </div>
  );
}
